/**
 * Reusable component representing a list item with a title and an
 * optional subtitle with a right-facing chevron.
 */

import * as React from 'react'

import { IconButton } from '@doordash/component-button'
import ComponentListCell from '@doordash/component-list-cell'
import { Colors, Inset, Icon } from '@doordash/design-language'
import {
  StyledListCellMenuItem,
  StyledStatusIndicator,
  CircleIconIndicatorContainer,
} from './styles'
import CircleIcon from './CircleIcon'
import { DeliveryIssueStatus } from '../../../../models/types'

type StatusIndicator = {
  status: DeliveryIssueStatus
}

// This circle indicator icon is missing in DLS.
// adding a margin-left of 3px to simulate list-cells api for iconStart
// theres some slight padding so if we use dls-icons here the indiactors will push content around
// The smallest available size via "Tiny" is 12 but we want size 8 here
export const StatusIndicator: React.FC<StatusIndicator> = ({ status }) => {
  return (
    // Only show blue or white circle on read/unread. everythign else show dispute icon
    <StyledStatusIndicator>
      {status === DeliveryIssueStatus.Unread ||
      status === DeliveryIssueStatus.Read ? (
        <CircleIconIndicatorContainer>
          <CircleIcon
            color={
              status === DeliveryIssueStatus.Unread
                ? Colors.SystemBlue60
                : Colors.SystemWhite
            }
            size={8}
          />
        </CircleIconIndicatorContainer>
      ) : (
        <Icon type={Icon.Types.ReceiptFill} size={Icon.Sizes.Medium} />
      )}
    </StyledStatusIndicator>
  )
}

type ListCellMenuItemProps = {
  status?: DeliveryIssueStatus
  subtitle?: string
  title: string
  onClick?: () => void
  separatorType?: string
}

export const ListCellMenuItem: React.FC<ListCellMenuItemProps> = props => (
  <StyledListCellMenuItem status={props.status}>
    <ComponentListCell
      renderBeforeContent={
        /**
         * Show status indicator if there is a status. Otherwise, show nothing
         * so other uses of ListCellMenuItem don't have an indentation.
         */
        Object.values(DeliveryIssueStatus).includes(props.status)
          ? () => <StatusIndicator status={props.status} />
          : undefined
      }
      title={props.title}
      secondaryTitleEnd={props.subtitle ? props.subtitle : ''}
      secondaryTitleALignment={ComponentListCell.Alignment.Center}
      size={ComponentListCell.Sizes.Small}
      iconEnd={{
        type: ComponentListCell.IconTypes.ChevronRight,
        accessibilityLabel: `Go to: ${props.title}`,
        size: IconButton.Sizes.Large,
        color: Colors.TextTertiary,
      }}
      insetSize={Inset.Sizes.Small}
      separatorType={ComponentListCell.SeparatorTypes.FullBorder}
      titleMaxLines={2}
      {...props}
    />
  </StyledListCellMenuItem>
)

export default ListCellMenuItem
